/**
 * Class representing EventTracker.
 */
class EventTracker {
  /**
   * Create an EventTracker instance.
   */
  constructor() {
    this.minInterval = 250;
    this.shouldTriggerView = true;
    this.shouldSkipNextPause = true;
    this.shouldSkipNextPlay = true;
    this.eventMap = new Map();
  }

  /**
   * Resets Event Tracker.
   */
  clear() {
    this.shouldTriggerView = true;
    this.shouldSkipNextPause = true;
    this.shouldSkipNextPlay = true;
    this.eventMap.clear();
  }

  /**
   * Returns the ellapsed time since the last occurrence of an event.
   * @param {string} eventName - the name of the event
   * @return {integer}
   */
  ellapsedTime(eventName) {
    if (!this.eventMap.has(eventName)) {
      return -1;
    }

    const timestamp = this.eventMap.get(eventName);

    return Date.now() - timestamp;
  }

  /**
   * Checks if ellapsed time is greater than the minimum interval.
   * @param {string} eventName - the name of the event
   * @return {boolean}
   */
  shouldSkip(eventName) {
    if (!this.eventMap.has(eventName)) {
      return false;
    }

    return this.ellapsedTime(eventName) < this.minInterval;
  }

  /**
   * Returns true if event should not pushed to queue, false otherwise.
   * This method also triggers the 'view' event based on the first occurrence of:
   * - 'play'
   * - 'ads-ima-start'
   * - 'played-q0'
   * @param {string} eventName - the name of the event
   * @param {Object} player - videojs player.
   * @param {Object} source - Video Embed source.
   * @return {boolean}
   */
  shouldSkipEvent(eventName, player, source) {
    if (this.shouldSkip(eventName)) {
      this.eventMap.set(eventName, Date.now());
      return true;
    }

    this.eventMap.set(eventName, Date.now());

    switch (eventName) {
    case 'play':
      if (this.shouldTriggerView) {
        player.trigger('view');
        this.shouldTriggerView = false;
      }
      if (this.shouldSkipNextPlay) {
        this.shouldSkipNextPlay = false;
        return true;
      }
      if (Math.floor(player.currentTime()) === 0) {
        return true;
      }
      return false;

    case 'pause':
      if (this.shouldSkipNextPause) {
        this.shouldSkipNextPause = false;
        return true;
      }
      if (Math.floor(player.currentTime()) === 0) {
        return true;
      }
      return false;

    case 'ads-ima-start':
      if (this.shouldTriggerView) {
        player.trigger('view');
        this.shouldTriggerView = false;
      }
      break;

    case 'ads-ima-loaded':
      this.shouldSkipNextPlay = true;
      this.shouldSkipNextPause = true;
      break;

    case 'adserror':
      if (!source.adTagUrl) {
        return true;
      }
      return false;

    case 'played-q0':
      this.shouldSkipNextPlay = false;
      this.shouldSkipNextPause = false;
      if (this.shouldTriggerView) {
        player.trigger('view');
        this.shouldTriggerView = false;
      }
      break;

    case 'played-q4':
      this.shouldTriggerView = true;
      this.shouldSkipNextPause = true;
      break;

    default:
      break;
    }

    return false;
  }
}

export default EventTracker;
